"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalVideoInputSelect = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ui_react_1 = require("@streem/ui-react");
const rxjs_1 = require("../../../../hooks/rxjs");
const room_session_1 = require("../../../../context/room_session");
const use_local_video_controller_state_1 = require("../../../../hooks/use_local_video_controller_state");
const src_1 = require("@streem/toolbox/src");
const logger_1 = require("../../../../util/logger");
const log = logger_1.logger.extend('InCallLocalVideoInputSelect');
const LocalVideoInputSelect = () => {
    const theme = (0, ui_react_1.useTheme)();
    const roomSession = (0, room_session_1.useRoomSession)();
    const [localVideoLabel] = (0, rxjs_1.useObservable)(roomSession.media.localVideoLabel);
    const [localVideoController] = (0, rxjs_1.useObservable)(roomSession.media.localVideoController);
    const [localVideoControllerState] = (0, use_local_video_controller_state_1.useLocalVideoControllerState)(roomSession.media.localVideoController);
    const [localDevices] = (0, rxjs_1.useObservable)(roomSession.media.localDevices);
    const [cameraSelectValue, setCameraSelectValue] = (0, react_1.useState)('');
    const [showTooltip, setShowTooltip] = (0, react_1.useState)(false);
    const localCameraDevices = (0, react_1.useMemo)(() => {
        if (Array.isArray(localDevices) && localDevices.length > 0) {
            return localDevices.filter(device => device.kind === 'videoinput');
        }
        return [];
    }, [localDevices]);
    const areLocalCameraDevicesAvailable = (0, react_1.useMemo)(() => {
        return (localCameraDevices.filter(device => device.deviceId || device.deviceId !== '').length >
            0);
    }, [localCameraDevices]);
    (0, react_1.useEffect)(() => {
        const setupCameraAndDevices = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const preferredCameraId = (0, src_1.getPreferredVideoInputId)();
                const attachedDevice = localCameraDevices.length > 0
                    ? localCameraDevices.find(device => device.deviceId === preferredCameraId)
                    : undefined;
                if (preferredCameraId && attachedDevice) {
                    setCameraSelectValue(preferredCameraId);
                }
            }
            catch (error) {
                log.error('error setting up camera and devices', error);
            }
        });
        setupCameraAndDevices();
    }, [localCameraDevices]);
    (0, react_1.useEffect)(() => {
        var _a;
        // if we don't have a preferred camera selected or the device is not attached, check for which one is currently in use to show the checkmark in the select dropdown
        if (localVideoLabel && !cameraSelectValue && localCameraDevices.length > 0) {
            setCameraSelectValue(((_a = localCameraDevices.find(device => device.label === localVideoLabel)) === null || _a === void 0 ? void 0 : _a.deviceId) || '');
        }
    }, [localVideoLabel, cameraSelectValue, localCameraDevices]);
    const show = (0, react_1.useCallback)(() => {
        if (!areLocalCameraDevicesAvailable) {
            setShowTooltip(true);
        }
    }, [areLocalCameraDevicesAvailable]);
    const hide = (0, react_1.useCallback)(() => setShowTooltip(false), []);
    if (!localVideoController) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {});
    }
    return ((0, jsx_runtime_1.jsx)(ui_react_1.Tooltip, Object.assign({ showTooltip: showTooltip, message: (0, jsx_runtime_1.jsx)(ui_react_1.AppText, Object.assign({ size: "small" }, { children: "No Video Devices Detected" })), distance: 20 }, { children: (0, jsx_runtime_1.jsx)(SelectControlWrapper, Object.assign({ onMouseEnter: show, onMouseOver: show, onMouseLeave: hide, onMouseOut: hide, onFocus: show, onBlur: hide }, { children: (0, jsx_runtime_1.jsx)(ui_react_1.Select, { isDisabled: !areLocalCameraDevicesAvailable, isSearchable: false, menuPlacement: "top", onSelect: (option) => __awaiter(void 0, void 0, void 0, function* () {
                    setCameraSelectValue(option.value);
                    yield (localVideoController === null || localVideoController === void 0 ? void 0 : localVideoController.restart({
                        deviceId: option.value,
                    }));
                }), options: localCameraDevices.map(device => ({
                    value: device.deviceId,
                    label: device.label,
                })), components: {
                    IndicatorSeparator: () => null,
                    MenuList: (_a) => {
                        var { children } = _a, props = __rest(_a, ["children"]);
                        return ((0, jsx_runtime_1.jsxs)(ui_react_1.ReactSelectComponents.MenuList, Object.assign({}, props, { children: [(0, jsx_runtime_1.jsx)(ui_react_1.AppText, Object.assign({ "data-testid": "select-camera", bold: true, style: { padding: '8px 12px' } }, { children: "Select a Camera" })), children] })));
                    },
                    DropdownIndicator: (_a) => {
                        var { children } = _a, props = __rest(_a, ["children"]);
                        const innerProps = Object.assign({}, props.innerProps, {
                            'data-testid': 'camera-select-dropdown',
                        });
                        return ((0, jsx_runtime_1.jsx)(ui_react_1.ReactSelectComponents.DropdownIndicator, Object.assign({}, props, { innerProps: innerProps }, { children: children })));
                    },
                }, styles: {
                    menu: (provided) => (Object.assign(Object.assign({}, provided), { width: '220px', right: '8px', padding: '0px' })),
                    valueContainer: (base) => (Object.assign(Object.assign({}, base), { padding: '0px' })),
                    control: (base, { selectProps }) => (Object.assign(Object.assign({}, base), { borderWidth: '0px', boxShadow: 'none', ':focus': {
                            borderWidth: '0px',
                        }, background: theme.colors.white, cursor: selectProps.isDisabled ? 'not-allowed' : 'pointer', pointerEvents: selectProps.isDisabled ? 'auto' : null })),
                    dropdownIndicator: (base, { selectProps }) => (Object.assign(Object.assign({}, base), { color: selectProps.isDisabled
                            ? theme.colors.grey10
                            : (localVideoControllerState === null || localVideoControllerState === void 0 ? void 0 : localVideoControllerState.enabled)
                                ? theme.colors.azure
                                : theme.colors.red50, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem top 0.5rem', backgroundSize: '20px 20px', paddingRight: '0px', marginLeft: '-6px', transform: 'rotate(180deg)', ':hover, :active': {
                            color: selectProps.isDisabled
                                ? theme.colors.grey10
                                : (localVideoControllerState === null || localVideoControllerState === void 0 ? void 0 : localVideoControllerState.enabled)
                                    ? theme.colors.azureTransparent
                                    : theme.colors.red50Transparent,
                        } })),
                    option: (base) => (Object.assign(Object.assign({}, base), { backgroundColor: 'none', ':hover': {
                            backgroundColor: theme.colors.blue05,
                            cursor: 'pointer',
                        } })),
                }, formatOptionLabel: (option) => ((0, jsx_runtime_1.jsxs)(OptionContainer, Object.assign({ "data-testid": `camera-option-${option.label}${option.value === cameraSelectValue ? '-selected' : ''}` }, { children: [option.value === cameraSelectValue && ((0, jsx_runtime_1.jsx)(ui_react_1.AppIcon, { name: "DoneIcon", "data-testid": "camera-option-checkmark", color: theme.colors.black, style: { alignSelf: 'center', flexGrow: 1 } })), (0, jsx_runtime_1.jsx)(OptionText, { children: option.label })] }))) }) })) })));
};
exports.LocalVideoInputSelect = LocalVideoInputSelect;
const SelectControlWrapper = ui_react_1.styled.div(() => ({
    padding: '2px',
}));
const OptionContainer = (0, ui_react_1.styled)('div')({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
});
const OptionText = (0, ui_react_1.styled)(ui_react_1.AppText)({
    textAlign: 'right',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
});
//# sourceMappingURL=local_video_input_select.js.map